import { usePage } from '@inertiajs/vue3'
import { computed, inject, watch } from 'vue'

/**
 * PostHog Vue Composable
 * 提供 PostHog 分析功能的便捷封装
 */
export function usePostHog() {
  const posthog = inject('posthog')
  const page = usePage()

  // 当前用户
  const currentUser = computed(() => page.props.currentUser)

  // 识别用户
  const identify = (user = null) => {
    const u = user || currentUser.value
    if (!posthog || !u) return

    posthog.identify(u.id, {
      email: u.email,
      name: u.name,
      phone: u.phone,
      created_at: u.created_at
    })
  }

  // 重置用户（登出时调用）
  const reset = () => {
    if (!posthog) return
    posthog.reset()
  }

  // 追踪事件
  const capture = (eventName, properties = {}) => {
    if (!posthog) return
    posthog.capture(eventName, properties)
  }

  // 设置用户属性
  const setPersonProperties = (properties) => {
    if (!posthog) return
    posthog.setPersonProperties(properties)
  }

  // 设置一次性用户属性（只在首次设置时生效）
  const setPersonPropertiesOnce = (properties) => {
    if (!posthog) return
    posthog.setPersonPropertiesForFlags(properties)
  }

  // 获取 Feature Flag
  const getFeatureFlag = (flagKey) => {
    if (!posthog) return null
    return posthog.getFeatureFlag(flagKey)
  }

  // 检查 Feature Flag 是否启用
  const isFeatureEnabled = (flagKey) => {
    if (!posthog) return false
    return posthog.isFeatureEnabled(flagKey)
  }

  // 重新加载 Feature Flags
  const reloadFeatureFlags = () => {
    if (!posthog) return
    posthog.reloadFeatureFlags()
  }

  // 监听 Feature Flag 变化
  const onFeatureFlags = (callback) => {
    if (!posthog) return
    posthog.onFeatureFlags(callback)
  }

  // 开始 Session Recording
  const startSessionRecording = () => {
    if (!posthog) return
    posthog.startSessionRecording()
  }

  // 停止 Session Recording
  const stopSessionRecording = () => {
    if (!posthog) return
    posthog.stopSessionRecording()
  }

  // 设置分组（用于 Group Analytics）
  const group = (groupType, groupKey, properties = {}) => {
    if (!posthog) return
    posthog.group(groupType, groupKey, properties)
  }

  // 追踪表单提交
  const captureFormSubmit = (formName, success, properties = {}) => {
    capture('form_submitted', {
      form_name: formName,
      success,
      ...properties
    })
  }

  // 追踪按钮点击
  const captureButtonClick = (buttonName, properties = {}) => {
    capture('button_clicked', {
      button_name: buttonName,
      ...properties
    })
  }

  // 追踪搜索
  const captureSearch = (query, resultsCount, properties = {}) => {
    capture('search_performed', {
      query,
      results_count: resultsCount,
      ...properties
    })
  }

  // 追踪错误
  const captureError = (errorType, errorMessage, properties = {}) => {
    capture('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      ...properties
    })
  }

  // 监听用户变化，自动识别
  watch(
    currentUser,
    (newUser, oldUser) => {
      if (newUser && newUser.id !== oldUser?.id) {
        identify(newUser)
      } else if (!newUser && oldUser) {
        reset()
      }
    },
    { immediate: true }
  )

  return {
    posthog,
    // 核心方法
    identify,
    reset,
    capture,
    // 用户属性
    setPersonProperties,
    setPersonPropertiesOnce,
    // Feature Flags
    getFeatureFlag,
    isFeatureEnabled,
    reloadFeatureFlags,
    onFeatureFlags,
    // Session Recording
    startSessionRecording,
    stopSessionRecording,
    // Group Analytics
    group,
    // 便捷追踪方法
    captureFormSubmit,
    captureButtonClick,
    captureSearch,
    captureError
  }
}

export default usePostHog
